import { AccountBean, AgencyBean, CustomizationBean } from 'common/api/api';
import { infoApi } from 'common/api/entitiesApi.tsx';
import { ColorPalette, colorPaletteManager, defaultColorPalette } from 'common/util/colorPalette.ts';
import { userUpdateStore } from 'common/util/userUpdateStore.tsx';
import React, { useContext, useEffect, useState } from 'react'

import RoleEnum = AccountBean.RoleEnum
import FeatureAccessEnum = AgencyBean.FeatureAccessEnum

const DEFAULT_TITLE = document.title

const DEFAULT_FAVICON_HREF = (() => {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]')
  return link?.href
})()

const normalize = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

const applyDocumentTitle = (title?: string) => {
  const next = title ?? DEFAULT_TITLE
  if (document.title !== next) document.title = next
  return next
}

const applyFavicon = (customHref?: string) => {
  const baseHref = customHref ?? DEFAULT_FAVICON_HREF
  if (!baseHref) return

  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]'))

  if (links.length === 0) {
    const link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
    links.push(link)
  }

  const href = customHref ? baseHref : `${baseHref}${baseHref.includes('?') ? '&' : '?'}v=${Date.now()}`

  links.forEach((link) => {
    if (link.href !== href) link.href = href
  })
}

export interface User {
  id: number
  name: string
  email: string
  role?: RoleEnum
  advertiserId?: number
  agencyId?: number
  agencyPublicId?: string
  timezoneId: string
  active: boolean
}

const Context = React.createContext<
  | {
      user?: User
      setUser: (user: User) => void
      blackTheme?: Boolean
      setBlackTheme: (black: Boolean) => void
      colorPalette: ColorPalette
      logoDataURI: string | undefined
      faviconDataURI: string | undefined
      customizationLoaded: boolean
      featureAccess: FeatureAccessEnum[] | undefined
      hasLinkedSystemSsps: boolean
      agencyRecognized: boolean
      advertiserSelfRegistration: boolean
      appTitle: string
    }
  | undefined
>(undefined)

export const useUserContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

const UserProvider = ({ children, agency }) => {
  const [user, setUser] = useState<User>()
  const [blackTheme, setBlackTheme] = useState<Boolean>()
  const [colorPalette, setColorPalette] = useState<ColorPalette>(defaultColorPalette)
  const [logoDataURI, setLogoDataURI] = useState<string | undefined>(undefined)
  const [faviconDataURI, setFaviconDataURI] = useState<string | undefined>(undefined)
  const [customizationLoaded, setCustomizationLoaded] = useState(false)
  const [featureAccess, setFeatureAccess] = useState<FeatureAccessEnum[] | undefined>([])
  const [hasLinkedSystemSsps, setHasLinkedSystemSsps] = useState(false)
  const [agencyRecognized, setAgencyRecognized] = useState(false)
  const [advertiserSelfRegistration, setAdvertiserSelfRegistration] = useState(false)
  const [appTitle, setAppTitle] = useState<string>(DEFAULT_TITLE)

  const applyPalette = (settings: Partial<ColorPalette>) => {
    const palette = colorPaletteManager.normalize(settings)
    setColorPalette(palette)
    colorPaletteManager.update(palette)
  }

  const refreshUserInfo = () =>
    infoApi
      .agencyInfo()
      .then((bean: CustomizationBean) => {
        applyPalette(bean.settings as Partial<ColorPalette>)

        const logoBase64 = normalize((bean as any)?.logo)
        setLogoDataURI(logoBase64 ? `data:image/png;base64,${logoBase64}` : undefined)

        const faviconBase64 = normalize((bean as any)?.favicon)
        const faviconUri = faviconBase64 ? `data:image/png;base64,${faviconBase64}` : undefined
        setFaviconDataURI(faviconUri)
        applyFavicon(faviconUri)

        setFeatureAccess(bean?.featureAccess)
        setHasLinkedSystemSsps(bean?.hasLinkedSystemSsps ?? false)
        setAgencyRecognized(bean?.agencyRecognized ?? false)
        setAdvertiserSelfRegistration(bean?.advertiserSelfRegistration ?? false)
        setAppTitle(applyDocumentTitle(normalize((bean as any)?.title)))
      })
      .catch(() => {
        setColorPalette(defaultColorPalette)
        colorPaletteManager.reset()

        setLogoDataURI(undefined)
        setFaviconDataURI(undefined)
        applyFavicon(undefined)

        setHasLinkedSystemSsps(false)
        setAgencyRecognized(false)
        setAdvertiserSelfRegistration(false)
        setAppTitle(applyDocumentTitle())
      })
      .finally(() => {
        setCustomizationLoaded(true)
      })

  useEffect(() => {
    if (agency) {
      setColorPalette(defaultColorPalette)
      colorPaletteManager.reset()

      setLogoDataURI(undefined)
      setFaviconDataURI(undefined)
      applyFavicon(undefined)

      setAppTitle(applyDocumentTitle())
    } else {
      refreshUserInfo()
      userUpdateStore.subscribe(refreshUserInfo)
    }
  }, [])

  useEffect(() => {
    if (user) {
      refreshUserInfo()
    }
  }, [user])

  return (
    <Context.Provider
      value={{
        user,
        setUser,
        blackTheme,
        setBlackTheme,
        colorPalette,
        logoDataURI,
        faviconDataURI,
        customizationLoaded,
        featureAccess,
        hasLinkedSystemSsps,
        agencyRecognized,
        advertiserSelfRegistration,
        appTitle,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export default UserProvider
